import React, { Component } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import V2Home from '@components/v2/V2Home'
import V2SelectProducts from '@components/v2/V2SelectProducts'
import V2Identification from '@components/v2/V2Identification'
import V2Resumo from '@components/v2/V2Resumo'
import V2Atualizacao from '@components/v2/V2Atualizacao'
import V2Cadastro from '@components/v2/V2Cadastro'
import V2Concluido from '@components/v2/V2Concluido'
import V2OrderList from '@components/v2/V2OrderList'
import V2TwoCards from '@components/v2/V2TwoCards'
import V2ContaPerfil from '@components/v2/V2ContaPerfil'
import V2ContaAtendimento from '@components/v2/V2ContaAtendimento'
import V2OrderDetail from '@components/v2/V2OrderDetail'
import V2AccountLogin from '@components/v2/V2AccountLogin'
import V2PrivateRoute from '@components/v2/V2PrivateRoute'
import V2AccountLayout from '@components/v2/V2AccountLayout'
import { V2ForgotPassword, V2PasswordRecovery } from '@components/v2/V2PasswordPages'

import Admin from '@containers/Admin'
import Faq from '@containers/Faq'
import FloatingWhatsApp from '../FloatingWhatsApp'
import { isV2Path, useV2Official, useV2OfficialResolved } from '../../utils/v2Official'
import { v2LandingFor, v2PathFromAdmin } from '@shared/v2Redirect'
import V2Assistant from '@components/v2/V2Assistant'
import Footer from '../Footer'
import Header from '../Header'
import Hero from '@components/Hero'
import Home from '@containers/Home'
import Loader from '@shared/Loader.js'
import Modal from '@shared/Modal.js'
import PrivateRoute from '@shared/PrivateRoute.js'
import Steps from '@containers/Steps'
import _ from 'lodash'
import { connect, useSelector } from 'react-redux'
import { fetchActiveNotification } from '@actions/activeNotification'
import { withRouter } from '../../utils/withRouter'

import Summary from '@containers/Summary'
import Appearance from '@containers/Appearance'
import Authorization from '@shared/Authorization'
import Contract from '@containers/Contract'
import EditDiscount from '@components/EditDiscount'
import NotificationForm from '@containers/Notification/Form'
import NotificationList from '@containers/Notification'
import Order from '@components/Order'
import NotFound from '../../components/NotFound'
import OrderDetail from '@components/OrderDetail'
import Report from '@containers/Report'
import School from '@containers/School'
import SchoolPaymentOptions from '@containers/SchoolPaymentOptions'
import SpecialDiscountForm from '@containers/SpecialDiscount/Form'
import SpecialDiscountList from '@containers/SpecialDiscount'
import VouchersInactivation from '@containers/VouchersInactivation'
import SAP from '@containers/SAP'
import Ticket from '@containers/Ticket'
import Variables from '@containers/Variables'
import Voucher from '@containers/Voucher'
import EditVoucher from '@components/EditVoucher'
import EditUser from '@components/EditUser'
import ConfirmProductsContainer from '@containers/ConfirmProductsContainer'
import DoneStep from '@components/DoneStep'
import Payment from '@components/Payment'
import Identification from '@components/Identification'
import SelectProductsContainer from '@containers/SelectProductsContainer'
import UserUpdateForm from '@components/UserUpdateForm'
import TransferList from '../TransferList'
import { TransferListProvider } from '../../hooks/useTransferList'
import Toast from '../../shared/Toast'
import OrderCreateCertificate from '../OrderCreateCertificate'
import OrderCertificates from '../OrderCertificates'
import PasswordRecovery from '../PasswordRecovery'
import ForgotPassword from '../ForgotPassword'
import CertificateSigned from '../CertificateSigned'
import GoogleCallback from '../GoogleCallback'

// V2 tem chrome próprio — esconde o Header V1 sob /v2 e na raiz quando a V2 é a
// experiência oficial (10/09: sem redirect; ver utils/v2Official).
// /v2/admin/pedidos/:order_id (link direto) → detalhe do pedido V2
const V2AdminOrderRedirect = () => {
  const { order_id } = useParams()
  return <Navigate to={`/v2/conta/pedidos/${order_id}`} replace />
}

// /admin/* → V2 (pedido do Eduardo, 16/10/2026), respeitando o kill switch
// CHECKOUT_V2_OFF: com o V2 desligado, o /admin segue no V1 (com o redirect por
// perfil do /admin puro). O /admin puro no V2 vai direto à página inicial do perfil
// ou, sem sessão, para /v2/admin (que leva ao login com redirect_to).
const AdminRoute = () => {
  const official = useV2OfficialResolved()
  const { pathname, search, hash } = useLocation()
  const { authenticated, user } = useSelector(state => state.sessionReducer || {})

  if (official === null) return null
  if (official) {
    const target = v2PathFromAdmin(pathname, search, hash) || (authenticated ? v2LandingFor(user) : '/v2/admin')
    return <Navigate to={target} replace />
  }

  const role = user && user.roles && user.roles[0]
  if (role && pathname.replace(/\//g, '') === 'admin') {
    const redirectTarget = role.id >= 2 ? '/admin/dashboard' : role.name === 'Comprador' ? '/admin/pedidos' : null
    if (redirectTarget) return <Navigate to={redirectTarget} replace />
  }
  return <PrivateRoute component={Admin} />
}

// /v2/admin puro → página inicial da área logada por perfil
const V2AdminLanding = () => {
  const user = useSelector(state => state.sessionReducer && state.sessionReducer.user)
  return <Navigate to={v2LandingFor(user)} replace />
}

const ChromeGate = ({ children }) => {
  const location = useLocation()
  const official = useV2Official()
  return isV2Path(location.pathname, official) ? null : children
}

// Suporte flutuante: WhatsApp no V1; nas rotas V2 o assistente guiado (que libera o
// WhatsApp quando não resolve).
const SupportWidget = () => {
  const location = useLocation()
  const official = useV2Official()
  return isV2Path(location.pathname, official) ? <V2Assistant /> : <FloatingWhatsApp />
}

// Raiz: home V2 direto (oficial) ou Hero + Home V1 quando CHECKOUT_V2 = '0'
const RootHome = () => {
  const official = useV2Official()
  return official ? (
    <V2Home />
  ) : (
    <>
      <Hero />
      <Home />
    </>
  )
}
const RootFooter = () => (useV2Official() ? null : <Footer />)

class App extends Component {
  componentDidMount() {
    const { pathname } = this.props.location
    window.onpopstate = e => {
      if (pathname.includes('/steps')) {
        window.localStorage.removeItem('reduxPersist:checkout')
        window.localStorage.removeItem('reduxPersist:preVoucher')
        window.location.href = '/'
      }
    }
    const shouldRedirectToHome =
      pathname.includes('/steps') && !pathname.includes('/steps/concluido') && _.isEmpty(this.props.checkout)
    if (shouldRedirectToHome) {
      if (_.isEmpty(this.props.checkout)) {
        window.localStorage.removeItem('reduxPersist:checkout')
        window.localStorage.removeItem('reduxPersist:preVoucher')
        window.location.href = '/'
      }
    }

    this.props.fetchActiveNotification()
  }


  // Rotas filhas da área administrativa — UM só conjunto, usado pelo /admin (chrome
  // V1) e pelo espelho /v2/admin (chrome V2). `base` parametriza o NotFound.
  _renderAdminChildRoutes(base) {
    return (
      <React.Fragment>
                <Route
                  exact
                  path="edit/:id/*"
                  element={
                    <PrivateRoute
                      component={() => (
                          <EditUser edit />
                      )}
                    />
                  }
                />
                <Route
                  exact
                  path="novo/*"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_users']}>
                          <EditUser edit={false} />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="dashboard"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_financial_dashboard']}>
                          <Summary />
                        </Authorization>
                      )}
                    />
                  }
                  exact
                />

                <Route path="pedidos" element={<PrivateRoute component={Order} exact />} />
                <Route
                  path="pedidos/:order_id"
                  exact
                  element={
                    // no espelho /v2/admin o detalhe do pedido é a tela V2 (link direto/favorito)
                    base === '/v2/admin/' ? (
                      <V2AdminOrderRedirect />
                    ) : (
                      <PrivateRoute
                        component={params => (
                          <Authorization permissions={['access_order_report', 'access_order']}>
                            <OrderDetail {...params} />
                          </Authorization>
                        )}
                      />
                    )
                  }
                />
                <Route
                  path="escolas/:school_id"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_school_report']}>
                          <School {...params} />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="escolas/:school_id/voucher"
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_school_report']}>
                          <Voucher {...params} />
                        </Authorization>
                      )}
                    />
                  }
                >
                  <Route path="novo" exact element={<EditVoucher edit={false} />} />
                  <Route path="edit/:id/*" exact element={<EditVoucher edit={true} />} />
                </Route>
                <Route
                  path="atendimento"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['open_case']}>
                          <Ticket />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="aparencia"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['theme_aspect_change']}>
                          <Appearance />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="relatorios/pedidos"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_order_report']}>
                          <Report title="Relatório de pedidos" subTitle="Pedidos" url="orders/list" type="orders" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="relatorios/vouchers"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_voucher_report']}>
                          <Report
                            title="Relatório de vouchers"
                            subTitle="Vouchers"
                            url={`voucher/${this.props.sessionReducer.user.school_id}/list`}
                            type="vouchers"
                          />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="relatorios/escolas"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_school_report']}>
                          <Report
                            title="Relatório de escolas"
                            subTitle="Escolas"
                            type="schools"
                            url="contracts/schools/list"
                          />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="relatorios/log-de-erros"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_log_error']}>
                          <Report
                            title="Log de erros"
                            subTitle="Logs"
                            type="sap_order_log"
                            url="orders/sap/report/list"
                          />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="sistema/fila"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_log_error']}>
                          <Report title="Fila" subTitle="Itens da fila" type="enqueue" url="enqueue/report/list" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="sistema/logs"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_log_error']}>
                          <Report title="Logs" subTitle="Logs do sistema" type="log" url="log/report/list" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="relatorios/usuarios"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_user_report']}>
                          <Report
                            title="Relatório de usuários"
                            subTitle="Usuários"
                            type="users"
                            url="users/report/list"
                          />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="contratos"
                  element={
                    <PrivateRoute
                      component={() => (
                        <Authorization permissions={['access_contract']}>
                          <Contract />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="escolas/desconto/:school_id"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_school_report']}>
                          <EditDiscount {...params} />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="special-discounts"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_special_discounts']}>
                          <SpecialDiscountList {...params} />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="special-discounts/novo"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['manage_special_discounts']}>
                          <SpecialDiscountForm {...params} title="Novo Desconto Professor" editMode="CREATE" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="special-discounts/:id"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['manage_special_discounts']}>
                          <SpecialDiscountForm {...params} title="Editar Desconto Professor" editMode="UPDATE" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="sistema/variables"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['manage_system_variables']}>
                          <Variables {...params} title="Variables" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="sap"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_system']}>
                          <SAP {...params} />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="notifications"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_notifications']}>
                          <NotificationList {...params} />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="notifications/novo"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['manage_notifications']}>
                          <NotificationForm {...params} title="Nova notificação" editMode="CREATE" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="notifications/:id"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['manage_notifications']}>
                          <NotificationForm {...params} title="Editar Notificação" editMode="UPDATE" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="school-payments"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['access_school_payment_options', 'manage_school_payment_options']}>
                          <SchoolPaymentOptions {...params} title="Meios de Pagamento" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="sistema/inativacao-vouchers"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <Authorization permissions={['manage_vouchers_inactivation']}>
                          <VouchersInactivation {...params} title="Inativação automática de vouchers e produtos" />
                        </Authorization>
                      )}
                    />
                  }
                />
                <Route
                  path="*"
                  exact
                  element={
                    <PrivateRoute
                      component={() => {
                        return <NotFound routeToRedirect={base} />
                      }}
                    />
                  }
                />

                <Route path="certificados-entrega" element={<PrivateRoute component={OrderCertificates} exact />} />
                <Route path="certificados-entrega/criar" element={<PrivateRoute component={OrderCreateCertificate} exact />} />
                <Route
                  path="certificados-entrega/visualizar/:certificateId"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <OrderCreateCertificate {...params} screenType={'visualizing'} />
                      )}
                    />
                  }
                />
                <Route
                  path="certificados-entrega/editar/:certificateId"
                  exact
                  element={
                    <PrivateRoute
                      component={params => (
                        <OrderCreateCertificate {...params} screenType={'editing'} />
                      )}
                    />
                  }
                />
      </React.Fragment>
    )
  }

  render() {
    let { sessionReducer } = this.props

    return (
      <div className="app">
        <Loader />
        <Modal />
        <SupportWidget />
        <div className="app__content">
          <ChromeGate>
            <Header />
          </ChromeGate>
          <Toast />
          <Routes>
            <Route
              exact
              path="/auth/google/callback"
              element={<GoogleCallback />}
            />
            <Route
              exact
              path="/esqueci-a-senha"
              element={
                <>
                  <ForgotPassword />
                </>
              }
            />
            <Route
              exact
              path="/recuperacao-senha"
              element={
                <>
                  <PasswordRecovery />
                </>
              }
            />
            <Route
              exact
              path="/transfer-list"
              element={<TransferList />}
            />
            <Route
              exact
              path="/certificate-signed"
              element={<CertificateSigned />}
            />
            <Route exact path="/" element={<RootHome />} />
            <Route exact path="/v2" element={<V2Home />} />
            <Route exact path="/v2/steps/selecionar-livros" element={<V2SelectProducts />} />
            <Route exact path="/v2/steps/identificacao" element={<V2Identification />} />
            <Route exact path="/v2/steps/cadastro" element={<V2Cadastro />} />
            <Route exact path="/v2/steps/resumo" element={<V2Resumo />} />
            <Route exact path="/v2/steps/atualizacao" element={<V2Atualizacao />} />
            <Route exact path="/v2/steps/concluido" element={<V2Concluido />} />
            <Route exact path="/v2/steps/pagamento-dois-cartoes" element={<V2TwoCards />} />
            {/* Área da conta V2 — só monta após a checagem de sessão (mesmo gate do /admin),
                para o PrivateRoute não redirecionar antes do rehydrate */}
            <Route exact path="/v2/conta/entrar" element={<V2AccountLogin />} />
            <Route exact path="/v2/conta/esqueci-a-senha" element={<V2ForgotPassword />} />
            <Route exact path="/v2/conta/recuperacao-senha" element={<V2PasswordRecovery />} />
            {sessionReducer.checked && (
              <Route exact path="/v2/conta/pedidos" element={<V2PrivateRoute component={V2OrderList} />} />
            )}
            {sessionReducer.checked && (
              <Route exact path="/v2/conta/pedidos/:order_id" element={<V2PrivateRoute component={V2OrderDetail} />} />
            )}
            {sessionReducer.checked && (
              <Route path="/v2/conta/perfil/:id/*" element={<V2PrivateRoute component={V2ContaPerfil} />} />
            )}
            {sessionReducer.checked && (
              <Route exact path="/v2/conta/atendimento" element={<V2PrivateRoute component={V2ContaAtendimento} />} />
            )}
            <Route
              exact
              path="desconto-professor"
              element={
                <>
                  <Hero specialDiscount />
                  <Home />
                </>
              }
            />
            <Route
              path="faq"
              element={
                <>
                  <Hero />
                  <Faq />
                </>
              }
            />
            <Route path="steps" element={<Steps />}>
              <Route path="selecionar-livros" element={<SelectProductsContainer />} />
              <Route path="confirmacao" element={<ConfirmProductsContainer />} />
              <Route path="identificacao" element={<Identification />} />
              <Route path="atualizacao" element={<UserUpdateForm />} />
              <Route path="pagamento" element={<Payment />} />
              <Route path="concluido" element={<DoneStep />} />
            </Route>
            {sessionReducer.checked && (
              <Route path="admin" element={<AdminRoute />}>
                {this._renderAdminChildRoutes('/admin/')}
              </Route>
            )}
            {/* Espelho V2 da área administrativa: MESMOS componentes e permissões,
                chrome V2 (V2AccountLayout) + skin CSS — nenhuma funcionalidade muda */}
            {sessionReducer.checked && (
              <Route path="v2/admin" element={<V2PrivateRoute component={V2AccountLayout} />}>
                <Route index element={<V2AdminLanding />} />
                {this._renderAdminChildRoutes('/v2/admin/')}
              </Route>
            )}
          </Routes>

          <Routes>
            <Route exact path="/" element={<RootFooter />} />
            <Route exact path="/esqueci-a-senha" element={<Footer />} />
            <Route exact path="/recuperacao-senha" element={<Footer />} />
            <Route exact path="/certificate-signed" element={<Footer />} />
            <Route path="admin/*" element={<Footer />} />
            <Route path="faq" element={<Footer />} />
            <Route path="steps/concluido" element={null} />
          </Routes>
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  checkout: state.checkout,
  sessionReducer: state.sessionReducer,
})

const mapDispatchToProps = dispatch => ({
  fetchActiveNotification: () => dispatch(fetchActiveNotification()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App))

